import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Package,
  Truck,
  MapPin,
  CreditCard,
  Mail,
  Calendar,
  ArrowLeft,
  RefreshCcw,
  HelpCircle,
  Box,
  ListOrdered,
  CheckCircle,
  XCircle,
  Phone,
  DollarSign,
} from "lucide-react";
import { useAdminAuth } from "@/context/Admin";
import { useAppDispatch } from "@/store/hooks";
import { updateOrderStatus, markOrderAsPaid } from "@/store/slices/orderSlice";
import Loader from "@/components/loader/loader";
import { formatCurrency } from "@/utils/format";

// Order Item Component with image error handling
function OrderItem({ item, index, isLast }) {
  const [imgError, setImgError] = useState(false);
  const imageUrl =
    item.product_image_url ||
    item.product_image ||
    item.image_url ||
    item.image;

  return (
    <div>
      <div className="flex items-center gap-4 py-2">
        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={item.product_name || "Product"}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <Package className="w-8 h-8 text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <p className="font-medium text-primary">
            {item.product_name || "Product"}
          </p>
          {item.variation && (
            <div className="text-xs text-gray-500 mt-1 space-y-0.5">
              {item.variation.variantName && (
                <div className="font-medium">{item.variation.variantName}</div>
              )}
              <div className="flex gap-2 flex-wrap">
                {item.variation.size && (
                  <span>Size: {item.variation.size}</span>
                )}
                {item.variation.color && (
                  <span>Color: {item.variation.color}</span>
                )}
                {item.variation.scent && (
                  <span>Scent: {item.variation.scent}</span>
                )}
              </div>
            </div>
          )}
          <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
        </div>
        <p className="font-bold text-primary">
          {formatCurrency(item.final_price * item.quantity)}
        </p>
      </div>
      {!isLast && <Separator />}
    </div>
  );
}

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const adminAuth = useAdminAuth();
  const dispatch = useAppDispatch();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Mark as paid dialog state
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [markPaidData, setMarkPaidData] = useState({
    transactionId: "",
    paymentMethod: "cash",
    notes: "",
  });

  // Safety check for admin context
  if (!adminAuth || !adminAuth.getOrderById) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-gray-500">
          Admin context not available. Please refresh the page.
        </p>
        <Button
          onClick={() => navigate("/admin/orders/list")}
          variant="outline"
        >
          Back to Orders
        </Button>
      </div>
    );
  }

  const { getOrderById } = adminAuth;

  // Fetch order data on mount
  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError("Order ID is required");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const orderData = await getOrderById(orderId);
        setOrder(orderData);
      } catch (err) {
        setError(err.message || "Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, getOrderById]);

  const handleStatusChange = async (newStatus) => {
    if (!orderId || updatingStatus || newStatus === order?.status) return;

    setUpdatingStatus(true);
    try {
      await dispatch(
        updateOrderStatus({ orderId, status: newStatus }),
      ).unwrap();
      // Update local state
      setOrder((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error("Failed to update order status:", err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!orderId || markPaidLoading) return;

    setMarkPaidLoading(true);
    try {
      await dispatch(
        markOrderAsPaid({
          orderId,
          transactionId: markPaidData.transactionId || undefined,
          paymentMethod: markPaidData.paymentMethod,
          notes: markPaidData.notes || undefined,
        }),
      ).unwrap();

      // Update local state
      setOrder((prev) => ({
        ...prev,
        paymentStatus: "paid",
        paymentTransactionId:
          markPaidData.transactionId || prev.paymentTransactionId,
        paymentMethod: markPaidData.paymentMethod || prev.paymentMethod,
      }));

      setMarkPaidDialogOpen(false);
      setMarkPaidData({ transactionId: "", paymentMethod: "cash", notes: "" });
    } catch (err) {
      console.error("Failed to mark order as paid:", err);
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // Parse shipping address (can be JSON string or object)
  const getShippingAddress = () => {
    if (!order?.shippingAddress) return null;

    let address = order.shippingAddress;

    // If it's a string, try to parse it
    if (typeof address === "string") {
      try {
        address = JSON.parse(address);
      } catch {
        // If parsing fails, return the string as raw
        return { raw: String(address) };
      }
    }

    // If it's already an object, process it
    if (typeof address === "object" && address !== null) {
      // Remove 'raw' property if it exists, but keep all other fields
      const { raw, ...addressFields } = address;

      // Check for common address fields to determine if this is a structured address
      const hasAddressFields =
        addressFields.address ||
        addressFields.address1 ||
        addressFields.street ||
        addressFields.streetAddress ||
        addressFields.city ||
        addressFields.state ||
        addressFields.country ||
        addressFields.zipCode ||
        addressFields.zip ||
        addressFields.postcode ||
        addressFields.postalCode;

      // If it has address fields, return the fields without raw (prioritize structured data)
      if (hasAddressFields) {
        return addressFields; // Return only the structured fields, not raw
      }

      // If no address fields but we have raw, return with raw
      if (raw) {
        return { raw: typeof raw === "string" ? raw : JSON.stringify(raw) };
      }

      // Check if the original address object has any meaningful data (including raw)
      const hasAnyData = Object.keys(address).some((key) => {
        if (key === "raw") return false; // Don't count raw as meaningful data
        const value = address[key];
        if (value === null || value === undefined || value === "") return false;
        if (typeof value === "object" && !Array.isArray(value)) {
          return value.value || value.label || value.name;
        }
        return true;
      });

      if (!hasAnyData) {
        return null;
      }

      // Return the object if it has some data (but exclude raw if we have structured fields)
      return addressFields;
    }

    return address;
  };

  // Get latest shipment info
  const getLatestShipment = () => {
    if (!order?.shipments || order.shipments.length === 0) return null;
    return order.shipments[0]; // Shipments are ordered DESC by date
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Package className="w-16 h-16 text-gray-400" />
        <p className="text-gray-500">{error || "Order not found."}</p>
        <Link to="/admin/orders/list">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Orders
          </Button>
        </Link>
      </div>
    );
  }

  const shippingAddress = getShippingAddress();
  const latestShipment = getLatestShipment();

  // Debug: Log shipping address to help diagnose issues
  // console.log('Shipping Address:', shippingAddress)

  // Get shipping contact info (prioritize shippingAddress, fallback to order fields)
  const getShippingContact = () => {
    if (shippingAddress) {
      return {
        name:
          shippingAddress.fullName ||
          shippingAddress.name ||
          (shippingAddress.firstName && shippingAddress.lastName
            ? `${shippingAddress.firstName} ${shippingAddress.lastName}`
            : null) ||
          order.shippingName ||
          order.user?.name,
        email:
          shippingAddress.email || order.shippingEmail || order.user?.email,
        phone:
          shippingAddress.phone || order.shippingPhone || order.user?.phone,
      };
    }
    return {
      name: order.shippingName || order.user?.name,
      email: order.shippingEmail || order.user?.email,
      phone: order.shippingPhone || order.user?.phone,
    };
  };

  const shippingContact = getShippingContact();

  const formatAddressValue = (value) => {
    if (value === null || value === undefined || value === "") return null;

    // If it's an object with a label property, return the label
    if (typeof value === "object" && value !== null) {
      // Handle arrays
      if (Array.isArray(value)) {
        const filtered = value.filter(Boolean);
        return filtered.length > 0 ? filtered.join(", ") : null;
      }

      // Handle objects with common properties
      if (value.label) return String(value.label).trim();
      if (value.value) return String(value.value).trim();
      if (value.name) return String(value.name).trim();

      // Try common address field names
      if (value.fullName) return String(value.fullName).trim();
      if (value.firstName || value.lastName) {
        const name = [value.firstName, value.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
        return name || null;
      }

      // Don't stringify complex objects - return null instead
      return null;
    }

    // If it's a string or number, return as is (trimmed)
    const str = String(value).trim();
    return str || null;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <ListOrdered className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-primary">Order Details</h1>
        </div>
        <Link to="/admin/orders/list">
          <Button
            variant="outline"
            className="border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Orders
          </Button>
        </Link>
      </div>
      <p className="text-gray-600">
        Detailed information and management for order {order.orderId || orderId}
        .
      </p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Order Summary & Products */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary break-all">
                Order {order.orderId || orderId}
              </CardTitle>
              <CardDescription className="flex items-center gap-2 flex-wrap">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(order.createdAt)}</span>
                <Separator orientation="vertical" className="h-4" />
                <Select
                  value={order.status}
                  onValueChange={handleStatusChange}
                  disabled={updatingStatus}
                >
                  <SelectTrigger className="w-[140px] h-8">
                    <SelectValue placeholder="Update Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Processing">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-yellow-700" />{" "}
                        Processing
                      </div>
                    </SelectItem>
                    <SelectItem value="Shipped">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-blue-700" /> Shipped
                      </div>
                    </SelectItem>
                    <SelectItem value="Delivered">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-700" />{" "}
                        Delivered
                      </div>
                    </SelectItem>
                    <SelectItem value="Cancelled">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-700" /> Cancelled
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Products List */}
              <div className="space-y-4">
                <h3 className="font-semibold text-primary text-lg">
                  Products in this Order
                </h3>
                {order.items && order.items.length > 0 ? (
                  order.items.map((item, index) => (
                    <OrderItem
                      key={item.product_id || index}
                      item={item}
                      index={index}
                      isLast={index === order.items.length - 1}
                    />
                  ))
                ) : (
                  <p className="text-gray-500">No items found</p>
                )}
              </div>

              <Separator />

              {/* Price Breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(order.subtotal)}</span>
                </div>
                {order.taxAmount > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Tax</span>
                    <span>{formatCurrency(order.taxAmount)}</span>
                  </div>
                )}
                {order.discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(order.discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>
                    {order.shippingCost === 0 || !order.shippingCost
                      ? "Free"
                      : formatCurrency(order.shippingCost)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold text-primary">
                  <span>Grand Total</span>
                  <span>{formatCurrency(order.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tracking Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Shipment Tracking</CardTitle>
              <CardDescription>
                Track the status of your delivery.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {latestShipment ? (
                <>
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-secondary" />
                    <div>
                      <p className="font-medium text-primary break-all">
                        Tracking Number: {latestShipment.tracking_number}
                      </p>
                      <p className="text-sm text-gray-600">
                        Courier: {latestShipment.courier || "N/A"}
                      </p>
                      {latestShipment.estimated_delivery && (
                        <p className="text-sm text-gray-600">
                          Estimated Delivery:{" "}
                          {formatDate(latestShipment.estimated_delivery)}
                        </p>
                      )}
                    </div>
                  </div>
                  <Link to={`/admin/orders/${orderId}/tracking`}>
                    <Button className="bg-primary hover:bg-primary/90 text-white">
                      <Truck className="w-4 h-4 mr-2" /> Update Tracking
                    </Button>
                  </Link>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="text-gray-500 flex items-center gap-2">
                    <Box className="w-5 h-5" />
                    <span>No tracking information available yet.</span>
                  </div>
                  <Link to={`/admin/orders/${orderId}/tracking`}>
                    <Button variant="outline" className="w-full">
                      <Truck className="w-4 h-4 mr-2" /> Add Tracking
                      Information
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Addresses & Payment */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {shippingAddress?.is_pickup
                  ? "Pickup Information"
                  : "Shipping Information"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contact Information */}
              <div className="space-y-3 pb-3 border-b border-gray-200">
                {shippingContact.name && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <Package className="w-4 h-4 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                        Recipient Name
                      </p>
                      <p className="font-semibold text-gray-900 break-words">
                        {shippingContact.name}
                      </p>
                    </div>
                  </div>
                )}
                {shippingContact.email && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <Mail className="w-4 h-4 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                        Email
                      </p>
                      <a
                        href={`mailto:${shippingContact.email}`}
                        className="text-sm text-primary hover:underline break-all"
                      >
                        {shippingContact.email}
                      </a>
                    </div>
                  </div>
                )}
                {shippingContact.phone && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <Phone className="w-4 h-4 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                        Phone
                      </p>
                      <a
                        href={`tel:${shippingContact.phone}`}
                        className="text-sm text-primary hover:underline break-words"
                      >
                        {shippingContact.phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Shipping Address */}
              {shippingAddress &&
                (() => {
                  // Check for structured address fields directly on shippingAddress
                  // (don't destructure raw yet, as it might not exist)
                  const addressLine1 =
                    formatAddressValue(shippingAddress.address) ||
                    formatAddressValue(shippingAddress.address1) ||
                    formatAddressValue(shippingAddress.street) ||
                    formatAddressValue(shippingAddress.streetAddress);
                  const addressLine2 =
                    formatAddressValue(shippingAddress.address2) ||
                    formatAddressValue(shippingAddress.street2);
                  const city = formatAddressValue(shippingAddress.city);
                  const state = formatAddressValue(shippingAddress.state);
                  const zip =
                    formatAddressValue(shippingAddress.postcode) ||
                    formatAddressValue(shippingAddress.zipCode) ||
                    formatAddressValue(shippingAddress.zip) ||
                    formatAddressValue(shippingAddress.postalCode);
                  const country = formatAddressValue(shippingAddress.country);

                  const hasStructuredFields =
                    addressLine1 ||
                    addressLine2 ||
                    city ||
                    state ||
                    zip ||
                    country;
                  const hasRaw = shippingAddress.raw;

                  // Only render if we have structured fields OR raw
                  if (!hasStructuredFields && !hasRaw) return null;

                  return (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                        Shipping Address
                      </p>
                      <div className="bg-gray-50 rounded-md p-3 space-y-1.5">
                        {/* Address Line 1 - try multiple field names */}
                        {addressLine1 && (
                          <p className="text-sm text-gray-900 font-medium">
                            {addressLine1}
                          </p>
                        )}
                        {/* Address Line 2 */}
                        {addressLine2 && (
                          <p className="text-sm text-gray-700">
                            {addressLine2}
                          </p>
                        )}
                        {/* City, State, ZIP */}
                        {(city || state || zip) && (
                          <div className="flex flex-wrap gap-1 text-sm text-gray-700">
                            {city && <span>{city}</span>}
                            {city && state && <span>,</span>}
                            {state && <span>{state}</span>}
                            {zip && (
                              <>
                                <span>•</span>
                                <span>{zip}</span>
                              </>
                            )}
                          </div>
                        )}
                        {/* Country */}
                        {country && (
                          <p className="text-sm text-gray-700 font-medium">
                            {country}
                          </p>
                        )}
                        {/* Fallback: Only show raw if NO structured fields were displayed */}
                        {!hasStructuredFields && hasRaw && (
                          <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                            {typeof shippingAddress.raw === "string"
                              ? shippingAddress.raw
                              : JSON.stringify(shippingAddress.raw, null, 2)}
                          </p>
                        )}
                        {/* Show message if nothing is available */}
                        {!hasStructuredFields && !hasRaw && (
                          <p className="text-sm text-gray-500 italic">
                            Address information not available
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {/* Fallback if no structured address */}
              {!shippingAddress && order.shippingAddress && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                    Shipping Address
                  </p>
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                      {typeof order.shippingAddress === "string"
                        ? order.shippingAddress
                        : JSON.stringify(order.shippingAddress, null, 2)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-primary">
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-secondary" />
                <p className="font-medium">
                  {order.paymentMethod || "Payment Method"}
                </p>
              </div>
              <Badge
                variant={
                  order.paymentStatus === "paid" ? "default" : "secondary"
                }
                className="ml-6"
              >
                {order.paymentStatus || "Unknown"}
              </Badge>
              {order.paymentTransactionId && (
                <p className="text-xs text-gray-500 ml-6 break-all">
                  Transaction ID: {order.paymentTransactionId}
                </p>
              )}
              <p className="text-sm text-gray-600">
                Charged on {formatDate(order.createdAt)}
              </p>
            </CardContent>
          </Card>

          {/* Order Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Admin Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Mark as Paid Button - only show if not already paid */}
              {order.paymentStatus !== "paid" && (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setMarkPaidDialogOpen(true)}
                >
                  <DollarSign className="w-4 h-4 mr-2" /> Mark as Paid
                </Button>
              )}
              <Link to={`/admin/orders/${orderId}/tracking`}>
                <Button className="w-full bg-primary hover:bg-primary/90 text-white">
                  <Truck className="w-4 h-4 mr-2" /> Update Tracking
                </Button>
              </Link>
              <Link to={`/admin/orders/${orderId}/tracking`}>
                <Button variant="outline" className="w-full">
                  <RefreshCcw className="w-4 h-4 mr-2" /> Update Order
                </Button>
              </Link>
              {order.user?.email && (
                <Button
                  variant="ghost"
                  className="w-full text-primary hover:text-secondary"
                  onClick={() =>
                    (window.location.href = `mailto:${order.user.email}`)
                  }
                >
                  <Mail className="w-4 h-4 mr-2" /> Contact Customer
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mark as Paid Dialog */}
      <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Mark Order as Paid</DialogTitle>
            <DialogDescription>
              Record the payment details for order {order?.orderId || orderId}.
              This will update the payment status and send a confirmation email
              to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="paymentMethod">Payment Method</Label>
              <Select
                value={markPaidData.paymentMethod}
                onValueChange={(value) =>
                  setMarkPaidData((prev) => ({ ...prev, paymentMethod: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paystack">Paystack</SelectItem>
                  <SelectItem value="flutterwave">Flutterwave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="transactionId">Transaction ID (Optional)</Label>
              <Input
                id="transactionId"
                value={markPaidData.transactionId}
                onChange={(e) =>
                  setMarkPaidData((prev) => ({
                    ...prev,
                    transactionId: e.target.value,
                  }))
                }
                placeholder="Enter transaction reference"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={markPaidData.notes}
                onChange={(e) =>
                  setMarkPaidData((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                placeholder="Add any notes about this payment..."
                rows={3}
              />
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Order Total:</span>
                <span className="font-bold">
                  {formatCurrency(order?.total || 0)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMarkPaidDialogOpen(false)}
              disabled={markPaidLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkAsPaid}
              disabled={markPaidLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {markPaidLoading ? "Processing..." : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
